import * as React from "react";

import {
  changePassword as changePasswordRequest,
  confirmPhoneCode,
  removeProfilePhoto,
  sendPhoneCode,
  setProfilePhoto,
  updateProfileDetails,
  type DetailsInput,
  type PhoneCodeResult,
} from "api/profile";
import { useAuth } from "hooks/useAuth";
import { avatarSrc } from "utils/avatar";

// Older builds kept an uploaded photo in this browser only; photos now live on the account.
const LEGACY_PHOTO_KEYS = ["customProfilePic", "useCustomProfilePic"];

/**
 * The profile drawer works on the signed-in user from the auth context (GET /api/profile) and
 * puts every server answer back there, so the top bar's name and photo update straight away.
 */
export const useProfileData = () => {
  const { user, status, applyProfile, setToken } = useAuth();

  React.useEffect(() => {
    try {
      LEGACY_PHOTO_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage blocked: nothing to clean up.
    }
  }, []);

  const saveDetails = React.useCallback(
    async (input: DetailsInput) => {
      const res = await updateProfileDetails(input);
      applyProfile(res.user);
      return res.changed;
    },
    [applyProfile]
  );

  /** The server ends every other session and returns a new token for this one. */
  const changePassword = React.useCallback(
    async (input: { currPassword: string; newPassword: string }) => {
      const res = await changePasswordRequest(input);
      setToken(res.token);
      applyProfile(res.user);
    },
    [applyProfile, setToken]
  );

  const requestPhoneCode = React.useCallback(
    async (input: { phoneNumber?: string; currPassword?: string } = {}) => {
      const res: PhoneCodeResult = await sendPhoneCode(input);
      applyProfile(res.user);
      return res;
    },
    [applyProfile]
  );

  const confirmPhone = React.useCallback(
    async (code: string) => {
      const res = await confirmPhoneCode(code);
      applyProfile(res.user);
      return res.changed;
    },
    [applyProfile]
  );

  const uploadPhoto = React.useCallback(
    async (photo: string) => applyProfile(await setProfilePhoto({ photo })),
    [applyProfile]
  );

  const chooseStockPhoto = React.useCallback(
    async (index: number) => applyProfile(await setProfilePhoto({ profilePicIndex: index })),
    [applyProfile]
  );

  const removePhoto = React.useCallback(
    async () => applyProfile(await removeProfilePhoto()),
    [applyProfile]
  );

  return {
    user,
    loading: status === "loading",
    photoSrc: avatarSrc(user),
    saveDetails,
    changePassword,
    requestPhoneCode,
    confirmPhone,
    uploadPhoto,
    chooseStockPhoto,
    removePhoto,
  };
};

export type ProfileData = ReturnType<typeof useProfileData>;
